import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import hero from "../assets/hero.jpg";

const HeroSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Hero Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-aquagreen/20 via-transparent to-blue-900/20" />
        <img
          src={hero}
          alt="Système aquaponique"
          className="w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-black/70 backdrop-blur-xs" />
      </div>

      {/* Content */}
      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        className="relative z-10 max-w-6xl mx-auto px-6 text-center text-white"
      >
        <motion.h1
          variants={itemVariants}
          className="text-5xl md:text-7xl font-bold mb-6 leading-tight"
        >
          Mangez mieux, local et durable{" "}
          <span className="text-aquagreen">à votre rythme</span>
        </motion.h1>

        <motion.p
          variants={itemVariants}
          className="text-xl md:text-2xl mb-8 opacity-90 max-w-4xl mx-auto leading-relaxed"
        >
          Un jardin aquaponique intelligent qui produit légumes et poissons,
          <br />
          directement chez vous. Sans effort, sans compromis, piloté par
          application.
        </motion.p>

        <motion.div
          variants={itemVariants}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12 max-w-3xl mx-auto"
        >
          <div className="flex flex-col items-center p-4 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
            <div className="text-2xl mb-2">🌱</div>
            <p className="text-sm font-medium">Qualité bio, sans pesticides</p>
          </div>
          <div className="flex flex-col items-center p-4 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
            <div className="text-2xl mb-2">🐟</div>
            <p className="text-sm font-medium">Truite fraîche sans métaux lourds</p>
          </div>
          <div className="flex flex-col items-center p-4 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
            <div className="text-2xl mb-2">💧</div>
            <p className="text-sm font-medium">Économiser 90% d'eau</p>
          </div>
          <div className="flex flex-col items-center p-4 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
            <div className="text-2xl mb-2">💸</div>
            <p className="text-sm font-medium">Rentabilisé en 12 à 18 mois</p>
          </div>
        </motion.div>

        <motion.div variants={itemVariants}>
        <motion.a
          href="https://forms.gle/gv7DaeyYDsvayxjXA"
          target="_blank"
          rel="noopener noreferrer"
          className="group relative inline-flex items-center px-12 py-4 bg-gradient-to-r from-aquagreen to-aquagreen text-aquablue font-bold text-lg rounded-full transition-all duration-300 shadow-2xl hover:shadow-aquagreen/25 hover:scale-105"
        >
          <span className="relative z-10">Devenir beta testeur</span>

          {/* Hover effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-aquagreen to-aquagreen rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* Animated pulsing layer */}
          <motion.div
            className="absolute inset-0 bg-white/20 rounded-full"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.a>
      </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center"
        >
          <div className="w-1 h-3 bg-white/60 rounded-full mt-2" />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default HeroSection;